/**
 * "Pay now" — the customer's no-login link to an EWI- invoice (EW-6b,
 * fieldiq-api POST / DELETE /earthworks/invoices/:id/pay-link). Shown for a
 * SENT invoice only; a draft has not gone to the customer and a void or paid
 * one has nothing to pay. The API refuses the same cases (invoice_not_sent,
 * invoice_void), and its refusal is what decides.
 *
 * Whether the customer can pay BY CARD is the API's `canPayByCard` (sent,
 * still owing, and the tenant's own Stripe can confirm a payment). Without it
 * the link still shows the invoice; it just offers no button.
 *
 * Admin / office staff make, copy and revoke; everyone else reads.
 */
import { useState } from 'react';
import { Copy, Link2, XCircle } from 'lucide-react';
import { useCreateEwPayLink, useRevokeEwPayLink } from '../../../hooks/useEwInvoices';
import { ewInstant, ewRefusal, fmtNumber } from '../../../lib/earthworks';
import type { EwInvoice } from '../../../types/ew-invoices';
import { EW_PRIMARY, EW_SECONDARY, EwPanel } from '../EwBits';

/** The panel is drawn for a sent invoice only. */
export const ewInvoiceShowsPayLink = (status: string | null | undefined) => status === 'sent';

export function EwInvoicePayLinkPanel({ inv, canWrite, tz }: { inv: EwInvoice; canWrite: boolean; tz: string | null }) {
  const create = useCreateEwPayLink();
  const revoke = useRevokeEwPayLink();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const link = inv.payLink;

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      setError('The link could not be copied. Select it and copy it by hand.');
    }
  };

  return (
    <EwPanel eyebrow="Customer" title="Payment link" testId="ew-invoice-pay-link">
      <div className="space-y-3 px-4 py-3 text-sm text-fieldiq-text">
        <p data-testid="ew-invoice-pay-card">
          {inv.canPayByCard
            ? 'The customer can pay the balance, or part of it, by card from this link.'
            : 'Card payment needs Stripe connected in Settings. The link still shows the customer the invoice.'}
        </p>
        {link ? (
          <>
            <p className="break-all font-mono text-white" data-testid="ew-invoice-pay-url">{link.url}</p>
            <p className="text-xs text-fieldiq-muted" data-testid="ew-invoice-pay-views">
              Made {ewInstant(link.createdAt, tz)} · opened {fmtNumber(link.viewCount)} {link.viewCount === 1 ? 'time' : 'times'} · last opened {ewInstant(link.lastViewedAt, tz)}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={EW_SECONDARY} onClick={copy} data-testid="ew-invoice-pay-copy">
                <Copy size={14} /> {copied ? 'Copied' : 'Copy link'}
              </button>
              {canWrite && (
                <button
                  type="button"
                  className={EW_SECONDARY}
                  disabled={revoke.isPending}
                  data-testid="ew-invoice-pay-revoke"
                  onClick={() => {
                    setError(null);
                    setCopied(false);
                    revoke.mutate(inv.id, { onError: (e) => setError(ewRefusal(e, 'The link could not be revoked.')) });
                  }}
                >
                  <XCircle size={14} /> {revoke.isPending ? 'Revoking…' : 'Revoke link'}
                </button>
              )}
            </div>
          </>
        ) : canWrite ? (
          <button
            type="button"
            className={EW_PRIMARY}
            disabled={create.isPending}
            data-testid="ew-invoice-pay-create"
            onClick={() => {
              setError(null);
              create.mutate(inv.id, { onError: (e) => setError(ewRefusal(e, 'The payment link could not be made.')) });
            }}
          >
            <Link2 size={14} /> {create.isPending ? 'Making link…' : 'Make payment link'}
          </button>
        ) : (
          <p className="text-fieldiq-muted" data-testid="ew-invoice-pay-none">No payment link has been made for this invoice.</p>
        )}
        {error && <p role="alert" className="text-fi-signal-amber" data-testid="ew-invoice-pay-error">{error}</p>}
      </div>
    </EwPanel>
  );
}
